import { useEffect, useRef, useState } from 'react';
import { atendimentoApi, type LinkPreview } from '../lib/api';

// Cache global no client — evita re-fetch dentro da mesma sessao do navegador.
const cacheCliente = new Map<string, LinkPreview | null>();

export function LinkPreviewCard({ url }: { url: string }) {
  const [preview, setPreview] = useState<LinkPreview | null>(() => cacheCliente.get(url) ?? null);
  const [pronto, setPronto] = useState<boolean>(() => cacheCliente.has(url));
  const [imgFalhou, setImgFalhou] = useState(false);
  const cancelado = useRef(false);

  useEffect(() => {
    if (cacheCliente.has(url)) {
      setPreview(cacheCliente.get(url)!);
      setPronto(true);
      return;
    }
    cancelado.current = false;
    atendimentoApi.linkPreview(url)
      .then((r) => {
        if (cancelado.current) return;
        cacheCliente.set(url, r.preview);
        setPreview(r.preview);
        setPronto(true);
      })
      .catch(() => {
        if (cancelado.current) return;
        cacheCliente.set(url, null);
        setPreview(null);
        setPronto(true);
      });
    return () => { cancelado.current = true; };
  }, [url]);

  // Enquanto carrega, nao renderiza nada (evita layout shift no chat).
  if (!pronto || !preview) return null;
  if (!preview.titulo && !preview.descricao && !preview.imagem) return null;

  const mostrarImagem = preview.imagem && !imgFalhou;
  // Sem imagem de previa, o icone do site numa miniatura quadrada a esquerda — o que o WhatsApp do
  // celular mostra. Esticado na faixa 16:9 de cima, o `cover` cortaria a logo.
  const mostrarIcone = !preview.imagem && preview.icone && !imgFalhou;

  return (
    <a
      href={preview.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block mb-1 rounded-lg overflow-hidden bg-white/60 border border-black/10 hover:bg-white/80 transition"
      style={{ maxWidth: 320 }}
      onClick={(e) => e.stopPropagation()}
    >
      {mostrarImagem && (
        <div className="w-full bg-black/5 overflow-hidden" style={{ maxHeight: 180, aspectRatio: '16 / 9' }}>
          <img
            src={preview.imagem!}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover"
            style={{ maxHeight: 180 }}
            onError={() => setImgFalhou(true)}
          />
        </div>
      )}
      <div className="px-2.5 py-1.5" style={mostrarIcone ? { display: 'flex', gap: 10, alignItems: 'center' } : undefined}>
        {mostrarIcone && (
          <img
            src={preview.icone!}
            alt=""
            loading="lazy"
            style={{ width: 56, height: 56, flex: '0 0 auto', objectFit: 'contain', borderRadius: 6 }}
            onError={() => setImgFalhou(true)}
          />
        )}
        <div style={{ minWidth: 0 }}>
        {preview.sitio && (
          <p className="text-[11px] text-gray-500 uppercase tracking-wide truncate">{preview.sitio}</p>
        )}
        {preview.titulo && (
          <p className="text-sm font-medium text-gray-800 line-clamp-2">{preview.titulo}</p>
        )}
        {preview.descricao && (
          <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{preview.descricao}</p>
        )}
        </div>
      </div>
    </a>
  );
}
